import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useGetUserClassroomsQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { useGetMyStandaloneCoursesQuery } from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import usePlatformFlags from '@Hooks/usePlatformFlags'
import Categories from '../Components/Categories'
import InstructorsCarousel from '../Components/InstructorsCarousel'
import FeaturedCoursesCarousel from '../Components/FeaturedCoursesCarousel'
import FeaturedBundlesCarousel from '@Features/Bundle/Components/FeaturedBundlesCarousel'
import { HomeWelcome, HomeProfileSteps, HomeBanners, HomeSearchBridge, HomeEmptyState } from './Components/HomeCards/HomeCards'
import MyClassroomsRail from './Components/MyClassroomsRail/MyClassroomsRail'
import MyCoursesRail from './Components/MyCoursesRail/MyCoursesRail'

/**
 * The logged-in home (design 01): welcome banner → profile-completion card →
 * admin banners → المدرسون rail → فصولي rail → كورساتي rail → featured
 * courses → search bridge → تصفح بالمادة chips. Sections gate on the platform
 * flags (classic / hybrid / courses-only); the rails self-hide when empty.
 * Per user decisions: no continue-learning card (no last-watched endpoint
 * yet), and the old Online/Exams/Revisions/Stats sections are dropped.
 */
const StudentLanding: React.FC = () => {
	const [t] = useTranslation('landingPage')
	const { instructor_section, subjects_section } = useAppSelector(s => s.settings)
	const { classroomsEnabled, coursesEnabled } = usePlatformFlags()
	const { data, isLoading, isFetching } = useGetUserClassroomsQuery(undefined, { skip: !classroomsEnabled })
	const myCourses = useGetMyStandaloneCoursesQuery(undefined, { skip: !coursesEnabled })

	// The explore empty-state shows only when the student owns NOTHING the
	// current platform mode can offer (classrooms and/or courses).
	const classroomsSettled = !classroomsEnabled || (!isLoading && !isFetching && (data?.data?.length ?? 0) === 0)
	const coursesSettled = !coursesEnabled || (!myCourses.isLoading && !myCourses.isFetching && (myCourses.data?.data?.length ?? 0) === 0)
	const nothingOwned = classroomsSettled && coursesSettled

	return (
		<Shell>
			<HomeWelcome />
			{nothingOwned && <HomeEmptyState />}
			<HomeProfileSteps />
			<HomeBanners />
			{instructor_section && <InstructorsCarousel variant="grid" heading={t('student_home.teachers', { defaultValue: 'المدرسون' })} />}
			{classroomsEnabled && <MyClassroomsRail />}
			<MyCoursesRail />
			<FeaturedCoursesCarousel />
			<FeaturedBundlesCarousel />
			<HomeSearchBridge />
			{subjects_section && <Categories compact heading={t('student_home.browse_subject', { defaultValue: 'تصفح بالمادة' })} />}
		</Shell>
	)
}

export default StudentLanding

/* Design shell: 512px centred column on mobile, 960px on desktop, 12px rhythm.
   The reused guest sections (teachers carousel, subject chips) bring their own
   inline padding for the full-bleed guest page — flush it here so everything
   lines up with the cards. */
const Shell = styled.div`
	width: 100%;
	max-width: 512px;
	margin: 0 auto;
	background: var(--surface-page);
	padding: 16px 16px 40px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	> section {
		padding-inline: 0;
	}
	@media (min-width: 1024px) {
		max-width: 960px;
		padding: 16px 24px 48px;
	}
`
